import { MultiSelect } from 'primereact/multiselect';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  UPDATE_COURSE,
  UPDATE_ADDITIONAL_SUBSCRIPTION,
  UPDATE_SUBSCRIPTIONS,
  ADD_COURSE
} from '../store/modules/course';
import {
  BaseCourseTypeSelector,
  StatusSelector,
  BaseChip,
  LastUpdated,
  CopyToClipboard,
  CoursesIncluded
} from './index';
import { formatDateToLastUpdated } from '../store/utils';
import { fetchGroupProducts } from '../store/api';
import './css/key-details-section.scss';

export const CourseKeyDetails = function () {
  const dispatch = useDispatch();
  const {
    subscriptionsList,
    coursesIncluded,
    data: { title, moduleType, status, locale, dateUpdated = new Date(), accessResources, id }
  } = useSelector((state) => state.course);

  useEffect(() => {
    if (moduleType === 'VID' && !coursesIncluded.length) {
      dispatch(ADD_COURSE());
    }
  }, [moduleType, coursesIncluded.length, dispatch]);

  // State for dynamic Additional subscriptions MultiSelect
  const [dynamicSubscriptionsList, setDynamicSubscriptionsList] = useState([]);
  const [dynamicSubscriptions, setDynamicSubscriptions] = useState([]);

  useEffect(() => {
    fetchGroupProducts('grp260')
      .then((data) => {
        const items = Array.isArray(data) ? data : (data?.content ?? data?.products ?? []);
        const list = items
          .filter((p) => !p.state || p.state.toUpperCase() === 'ENABLED')
          .map((p) => ({
            id: p.id,
            displayName: p.properties?.displayName ?? p.name ?? p.id,
            code: p.properties?.definition ?? p.id
          }));
        setDynamicSubscriptionsList(list);
      })
      .catch((err) => {
        console.error('fetchGroupProducts error:', err);
      });
  }, []);

  useEffect(() => {
    if (!dynamicSubscriptionsList.length) return;
    setDynamicSubscriptions(
      dynamicSubscriptionsList.filter((item) =>
        (accessResources ?? []).some((r) =>
          (r.code && r.code === item.code) || String(r.id) === String(item.id)
        )
      )
    );
  }, [dynamicSubscriptionsList, accessResources]);

  function handleUpdate({ key, value }) {
    dispatch(UPDATE_COURSE({ key, value }));
  }

  function handleTypeUpdate({ key, value }) {
    dispatch(UPDATE_COURSE({ key, value }));
    if (value === 'VID') {
      !coursesIncluded.length && addCourse();
    }
    if (value === 'MCQ') {
      dispatch(UPDATE_COURSE({ key: 'doi', value: '' }));
    }
  }

  function handleLastUpdated({ key, value }) {
    // the BE requested date format is "Month (short) date, full year" like Nov 16, 2022 (for example)
    dispatch(UPDATE_COURSE({ key, value: formatDateToLastUpdated(value) }));
  }

  function addCourse() {
    dispatch(ADD_COURSE());
  }
  function deleteCourse() {
    // dispatch(DELETE_COURSE_INCLUDED(id));
  }

  function handleUpdateSubscriptions(arr) {
    dispatch(UPDATE_SUBSCRIPTIONS(arr));
  }

  return (
    <section className="key-details-section" id="key-details" cy="course-key-details">
      <header className="flex flex-between">
        <h2>Key details</h2>
        <div className="flex-center">
          <BaseCourseTypeSelector value={moduleType} onChange={handleTypeUpdate} />

          <StatusSelector title status={status} onChange={handleUpdate} />
        </div>
      </header>

      <div>
        <label htmlFor="title">
          {moduleType === 'VID' ? 'Video title' : 'Course title'}
          <br />
          <textarea
            className="w100"
            cy="course-title"
            id="title"
            value={title}
            onInput={(ev) => handleUpdate({ key: 'title', value: ev.target.value })}
            placeholder={moduleType === 'VID' ? 'video title' : 'course title'}
          />
        </label>
        <span>Locale: {locale}</span>
      </div>

      <div className="unique-updated flex m-t-24">
        <div className="m-r-24">
          <label htmlFor="unique">
            Unique ID <br />
            <input
              type="text"
              cy="course-id"
              id="unique"
              value={id || ''}
              disabled
              onInput={() => { }}
            />
          </label>
          <CopyToClipboard />
        </div>
        <LastUpdated dateUpdated={dateUpdated} onChange={handleLastUpdated} />
      </div>

      {moduleType === 'VID' && <CoursesIncluded courses={coursesIncluded} addCourse={addCourse} isParentCourse={true} deleteCourse={deleteCourse} />}

      <div className="subscriptions-chips m-t-24" cy="subscriptions">
        <b>Subscriptions</b>
        <br />
        {subscriptionsList?.map(({ displayName, id }) => (
          <BaseChip
            label={displayName}
            id={id}
            key={id}
            selected={accessResources?.map((o) => o.id).some((val) => +val === +id)}
            section="keyDetails"
            onClick={handleUpdateSubscriptions}
          />
        ))}
      </div>

      <div id="dynamic-subscriptions" className="relative m-t-24">
        <label htmlFor="dynamic" cy="additional-subscriptions">
          Additional subscriptions <br />
          <MultiSelect
            inputId="dynamic"
            value={dynamicSubscriptions}
            options={dynamicSubscriptionsList}
            onChange={(e) => {
              setDynamicSubscriptions(e.value);
              dispatch(UPDATE_ADDITIONAL_SUBSCRIPTION(e.value));
            }}
            optionLabel="displayName"
            placeholder="Search all other subscriptions"
            display="chip"
            filter
            resetFilterOnHide
          />
        </label>
      </div>
    </section>
  );
};
